import type { CategoryId } from './category-colors';

export interface WidgetToolMeta {
  categoryId: CategoryId;
  /**
   * Agent name keyed in `agentConfiguration` (lib/config/agents.config.ts).
   * The artifact preview modal passes this to `getWidgetComponentSync` so
   * widget kinds not registered in `WIDGET_REGISTRY` (e.g. image/video
   * generation agents whose components live behind module-internal deps
   * wrappers) still resolve to the correct renderer.
   */
  agentName: string;
}

/**
 * Maps an artifact's widget `key` to the agent tool that produced it. Used by
 * the project Library + artifact preview surfaces to render the Tools column
 * with the agent name + category-colored badge. Unmapped keys (e.g.
 * `file_input_*`, third-party tools) fall back to a kind-palette badge.
 *
 * Keep in sync with widget keys exported from `libs/widgets/src/`. Both v1 and
 * v2 of the same widget family share the same tool meta (the version bump is
 * a schema migration, not a tool rename).
 */
export const WIDGET_TOOL_BY_KEY = {
  // eslint-disable-next-line i18next/no-literal-string -- agent identifier resolving the tool badge
  'ideation-widget:v1': { categoryId: 'create', agentName: 'ideation' },
  // eslint-disable-next-line i18next/no-literal-string -- agent identifier resolving the tool badge
  'ideation-widget:v2': { categoryId: 'create', agentName: 'ideation' },
  'image-generation-widget:v1': { categoryId: 'create', agentName: 'image-generation' },
  'video-generation-widget:v1': { categoryId: 'create', agentName: 'video-generation' },
  'copy-generation-widget:v1': { categoryId: 'create', agentName: 'copy-generation' },
  'strategic-territories:v1': { categoryId: 'scale', agentName: 'strategic-territories-agent' },
  'behind-the-answer-widget:v1': { categoryId: 'plan', agentName: 'audience-flow-agent' },
  'social-brief-widget:v1': { categoryId: 'plan', agentName: 'social-media-brief' },
  'audience-flow-widget:v1': { categoryId: 'plan', agentName: 'audience-flow-agent' },
  'four-cs-widget:v1': { categoryId: 'scale', agentName: 'strategic-territories-agent' },
  'brief-generation-widget:v1': { categoryId: 'plan', agentName: 'brief-generation' },
} as const satisfies Record<string, WidgetToolMeta>;

export function resolveArtifactTool(key: string): WidgetToolMeta | null {
  return (WIDGET_TOOL_BY_KEY as Record<string, WidgetToolMeta>)[key] ?? null;
}

/**
 * Tailwind class map for the tool badge (artifact preview modal header
 * + any other monks-ui surface). The shared `CATEGORY_COLORS` constant
 * exposes hex tokens consumed via MUI `sx`; surfaces built on monks-ui
 * cannot use MUI so we mirror the same palette as static Tailwind classes
 * here. Keep keys in sync with `CategoryId`.
 */
export const TOOL_BADGE_CLASSES_BY_CATEGORY = {
  plan: 'bg-orange-100 text-orange-900',
  create: 'bg-sky-100 text-sky-900',
  scale: 'bg-pink-100 text-pink-900',
  deliver: 'bg-blue-200 text-blue-900',
  support: 'bg-green-100 text-green-900',
} as const;
